import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useDesign } from "../../../context/DesignContext";
import { useContentLayout } from "../../../context/ContentMaxWidthPercentContext";
import {
  getLayoutContentWidthPx,
  maxWidthPercentToDesktopGridColumns,
  useResponsiveGridColumnCount,
} from "../../../Utils/contentWidthGridLayout";
import { isCreatorPagesMode } from "../../../Utils/componentsTable";
import {
  normalizeShoppableContentFilter,
} from "../StayUpToDateIndividuelComponent/RenderIndividualUI";
import "../StayUpToDateIndividuelComponent/RenderIndividualUI.css";

const CAROUSEL_TILE_WIDTH = 143;
const CAROUSEL_TILE_HEIGHT = 250;
const CAROUSEL_TILE_GAP = 12;
const GRID_TILE_HEIGHT = 250;
const MIN_POST_SKELETON_COUNT = 4;

type CarouselWidthOptions = {
  maxWidthPercent: number;
  narrowRemLayout: boolean;
  from?: string;
  embedShellLayout?: boolean;
};

/** Usable content width inside `.uptodate-wrapper` from viewport width. */
function getContentWidthFromScreen(
  screenWidth: number,
  options: CarouselWidthOptions
): number {
  const fromNorm = String(options.from || "").toLowerCase();
  const isCreatorPages = isCreatorPagesMode(options.from);
  const isEmbed =
    fromNorm === "embed-search" && Boolean(options.embedShellLayout);

  const shellMax = isCreatorPages
    ? getLayoutContentWidthPx(screenWidth, {
        maxWidthPercent: options.maxWidthPercent,
        isCreatorPage: true,
      }) + (screenWidth < 890 ? 0 : 60)
    : isEmbed
      ? getLayoutContentWidthPx(screenWidth, {
          maxWidthPercent: options.maxWidthPercent,
          isEmbed: true,
        }) + (screenWidth < 890 ? 0 : 60)
      : getLayoutContentWidthPx(screenWidth, {
          maxWidthPercent: options.maxWidthPercent,
        }) + (screenWidth < 890 ? 0 : 60);

  const horizontalPadding = screenWidth < 890 ? 0 : 60;
  return Math.max(0, shellMax - horizontalPadding);
}

/** Carousel skeleton tile count; 4.5 peek only on standalone / link-in-bio frontend. */
function getCarouselSkeletonItemCount(
  screenWidth: number,
  maxCols = 8,
  options: CarouselWidthOptions & { usePeek?: boolean } = {
    maxWidthPercent: 100,
    narrowRemLayout: false,
  }
): number {
  const contentWidth = getContentWidthFromScreen(screenWidth, options);
  if (contentWidth <= 0) return maxCols;
  const exactCols =
    (contentWidth + CAROUSEL_TILE_GAP) /
    (CAROUSEL_TILE_WIDTH + CAROUSEL_TILE_GAP);
  const count = options.usePeek
    ? Math.ceil(Math.floor(exactCols) + 0.5)
    : Math.floor(exactCols);
  return Math.max(1, Math.min(maxCols, count));
}

const DataSkeleton = (props: any) => {
  const {
    dashboard,
    arrayLength = 5,
    designTokens: designTokensProp,
    from,
    component,
    contentFilter: contentFilterProp,
    /** Individual collection/detail grid — fluid cells like RenderIndividualUI. */
    individualGrid = false,
    /** Inline landing expansion uses horizontal carousel, not grid. */
    expandedSlot = false,
  } = props;
  const { templateName, primaryColor, secondaryColor, tertiaryColor, fontFamily } =
    useDesign();
  const contextTokens = {
    templateName: templateName || "",
    primaryColor: primaryColor || "",
    secondaryColor: secondaryColor || "",
    tertiaryColor: tertiaryColor || "",
    fontFamily: fontFamily || "",
  };
  const designTokens = designTokensProp ?? contextTokens;

  const { maxWidthPercent, narrowRemLayout, embedShellLayout } = useContentLayout();
  const isCreatorPage = isCreatorPagesMode(from);
  const isEmbed =
    String(from || "").toLowerCase() === "embed-search" &&
    Boolean(embedShellLayout);
  const desktopCols = maxWidthPercentToDesktopGridColumns(
    maxWidthPercent,
    narrowRemLayout,
    { isCreatorPage, isEmbed }
  );
  const gridColumnCount = useResponsiveGridColumnCount(desktopCols, {
    maxWidthPercent,
    narrowRemLayout,
    isCreatorPage,
    isEmbed,
  });

  const contentFilter = normalizeShoppableContentFilter(
    contentFilterProp ?? component?.shoppable_content_filter
  );

  const [screenWidth, setScreenWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    let debounceId: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (debounceId) clearTimeout(debounceId);
      // Wait for resize to settle — avoid re-rendering skeleton tiles every frame.
      debounceId = setTimeout(() => {
        const next = window.innerWidth;
        setScreenWidth((prev) => (prev === next ? prev : next));
      }, 120);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      if (debounceId) clearTimeout(debounceId);
    };
  }, []);

  const isGridLayout = individualGrid && !expandedSlot;
  const isCreatorPages = isCreatorPagesMode(from);
  const carouselWidthOptions: CarouselWidthOptions = {
    maxWidthPercent,
    narrowRemLayout,
    from,
    embedShellLayout,
  };
  const useCarouselPeek =
    !dashboard &&
    !isCreatorPages &&
    String(from || "").toLowerCase() !== "embed-search";

  const effectiveArrayLength = dashboard
    ? arrayLength
    : isGridLayout
      ? Math.max(MIN_POST_SKELETON_COUNT, arrayLength, gridColumnCount)
      : Math.max(
          MIN_POST_SKELETON_COUNT,
          Math.min(
            arrayLength,
            getCarouselSkeletonItemCount(screenWidth, arrayLength, {
              ...carouselWidthOptions,
              usePeek: useCarouselPeek,
            })
          )
        );

  function SkeletonStatBox() {
    const forceDefault =
      !!dashboard ||
      (from && String(from).toLowerCase() === "dashboardcarouselstructure") ||
      (from && String(from).toLowerCase() === "embed-search");
    const isDefaultTheme =
      forceDefault || designTokens?.templateName?.toLowerCase() === "default";

    const cellContent = isDefaultTheme ? (
      <div
        className={!dashboard ? "m-0" : "m-0px"}
        style={{
          backgroundColor: "#ededed",
          borderRadius: ".5rem",
          width: isGridLayout ? "100%" : CAROUSEL_TILE_WIDTH,
          height: isGridLayout ? "100%" : undefined,
        }}
      >
        <Skeleton
          className={
            isGridLayout ? "individual-grid-skeleton__block" : "skeleton"
          }
          width={isGridLayout ? "100%" : CAROUSEL_TILE_WIDTH}
          height={isGridLayout ? "100%" : CAROUSEL_TILE_HEIGHT}
        />
      </div>
    ) : (
      <div
        className="grid-skeleton-card"
        style={{
          width: isGridLayout ? "100%" : CAROUSEL_TILE_WIDTH,
          height: isGridLayout ? "100%" : GRID_TILE_HEIGHT,
          borderRadius: "10px",
          border: designTokens?.tertiaryColor
            ? `1px solid ${designTokens?.tertiaryColor}`
            : "#ededed",
          boxSizing: "border-box",
        }}
      >
        <div
          className="grid-skeleton-image"
          style={{
            width: "100%",
            height: isGridLayout ? "100%" : "95%",
            margin: "0 auto",
          }}
        >
          {designTokens?.templateName &&
          designTokens?.templateName?.toLowerCase() != "default" ? (
            <div
              className={
                isGridLayout
                  ? "skeleton individual-grid-skeleton__block"
                  : "skeleton"
              }
            />
          ) : (
            <Skeleton
              className={
                isGridLayout ? "individual-grid-skeleton__block" : "skeleton"
              }
              width={isGridLayout ? "100%" : CAROUSEL_TILE_WIDTH}
              height={isGridLayout ? "100%" : CAROUSEL_TILE_HEIGHT}
              baseColor={
                designTokens?.templateName?.toLowerCase() === "default"
                  ? "#ededed"
                  : designTokens?.primaryColor
              }
              highlightColor={
                designTokens?.templateName?.toLowerCase() === "default"
                  ? "#ededed"
                  : designTokens?.primaryColor
              }
              style={{
                border: designTokens?.secondaryColor
                  ? `1px solid ${designTokens?.secondaryColor}`
                  : "#ededed",
                width: isGridLayout ? "100%" : undefined,
                height: isGridLayout ? "100%" : CAROUSEL_TILE_HEIGHT,
                borderRadius: "10px",
              }}
            />
          )}
        </div>
      </div>
    );

    if (isGridLayout) {
      return (
        <div className="render-individual-ui__cell individual-grid-skeleton-cell">
          {cellContent}
        </div>
      );
    }

    return cellContent;
  }

  const skeletonTiles = [...Array(effectiveArrayLength)].map((_, index) => (
    <SkeletonStatBox key={index} />
  ));

  if (isGridLayout) {
    const gridClassName = `render-individual-ui__grid render-individual-ui__grid--filter-${contentFilter}`;
    const shellClassName =
      narrowRemLayout && String(from || "").toLowerCase() !== "embed-search"
        ? "feed-optimized-width-no-page"
        : "";

    return (
      <div className={shellClassName}>
        <div
          className={`render-individual-ui render-individual-ui--skeleton render-individual-ui--filter-${contentFilter}`}
        >
          <div className="individual-outer-container render-individual-ui__outer--detail">
            <div className={gridClassName}>{skeletonTiles}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-skeleton-root data-skeleton-root--carousel">
      <div className="mainDiv data-skeleton-root__main">
        <div
          className="data-skeleton data-skeleton--carousel"
          style={{ gap: `${CAROUSEL_TILE_GAP}px` }}
        >
          {skeletonTiles}
        </div>
      </div>
    </div>
  );
};

export default DataSkeleton;
